import { Layout } from '@/shell/Layout'
import { GettingStarted } from '@/features/dashboard/components/GettingStarted'
import { SkeletonCard } from '@/shared/components/Skeleton'
import { FeedbackMessage } from '@/shared/components/FeedbackMessage'
import { PageSection } from '@/shared/components/PageSection'
import { useProfile, useTenantAnalytics } from '@/lib/hooks'
import { useSelectedOrg } from '@/context/SelectedOrgContext'
import { ManagedResourcesCard } from '@/features/dashboard/cards/ManagedResourcesCard'
import { OpenFindingsCard } from '@/features/dashboard/cards/OpenFindingsCard'
import { PatchAdoptionCard } from '@/features/dashboard/cards/PatchAdoptionCard'
import { PolicyPostureStrip } from '@/features/dashboard/cards/PolicyPostureStrip'
import { PushCadenceCard } from '@/features/dashboard/cards/PushCadenceCard'
import { RiskAllClearCard } from '@/features/dashboard/cards/RiskAllClearCard'
import { SlowestAppliesCard } from '@/features/dashboard/cards/SlowestAppliesCard'
import { TopRiskModulesCard } from '@/features/dashboard/cards/TopRiskModulesCard'
import { TopRiskWorkspacesCard } from '@/features/dashboard/cards/TopRiskWorkspacesCard'
import { TopTrendChart } from '@/features/dashboard/cards/TopTrendChart'

export function Dashboard() {
  const profile   = useProfile()
  const { selectedOrg, orgsLoading } = useSelectedOrg()
  const slug      = selectedOrg?.slug ?? null
  const analytics = useTenantAnalytics(slug)

  // Zero-org user: slug never resolves (SelectedOrgContext skips auto-init when
  // the org list is empty), so don't wait on analytics that will never load.
  if (slug === null && !orgsLoading) {
    return (
      <Layout title="Dashboard">
        <GettingStarted />
      </Layout>
    )
  }

  // Gate on identity, not just loading: `selectedOrg` and the analytics fetch
  // settle in separate commits, so there's a frame where `analytics.data` still
  // belongs to the previously-selected org. Rendering it then would show the
  // wrong org's dashboard. `fetchedFor === slug` is true only once the data we
  // hold was fetched for the org currently selected.
  const ready =
    !profile.loading &&
    !analytics.loading &&
    !orgsLoading &&
    analytics.fetchedFor === slug

  if (!ready) {
    return (
      <Layout title="Dashboard">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }} aria-hidden="true">
          <SkeletonCard rows={4} />
          <SkeletonCard rows={4} />
          <SkeletonCard rows={4} />
          <SkeletonCard rows={4} />
        </div>
      </Layout>
    )
  }

  if (analytics.error) {
    return (
      <Layout title="Dashboard">
        <FeedbackMessage ok={false} msg={analytics.error} />
      </Layout>
    )
  }

  const a = analytics.data

  const showOnboarding =
    (a?.total_downloads ?? 0) === 0 &&
    (a?.top_modules?.length ?? 0) === 0 &&
    (a?.state?.total_workspaces ?? 0) === 0

  return (
    <Layout title="Dashboard">
      {showOnboarding ? (
        <GettingStarted />
      ) : (
        <>
          {/* ── Activity ─────────────────────────────────────────────────────
              How much is going on, over time. Four charts that share an x-axis in spirit if not
              in scale, so they belong together rather than interleaved with the risk cards.

              It opens the page as the baseline the rest is read against: a critical finding on a
              module nobody pulls and one on a module pulled daily are the same row in the Risk
              section below, and only the volume here tells them apart. */}
          <PageSection title="Activity" note="90-day and 30-day windows">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <TopTrendChart
              kicker="Module Trend"
              title="Top module downloads (90 days)"
              helpTrack="help_downloads_trend"
              entities={a?.top_module_download_series ?? []}
              unit="downloads"
              legendHref={slug ? (name) => {
                const i = name.lastIndexOf('/')
                return `/org/${slug}/modules/${name.slice(0, i)}/${name.slice(i + 1)}`
              } : undefined}
              help={
                <>
                  <p>Daily downloads for your <strong>5 most-downloaded modules</strong> over
                  the last 90 days - a proxy for how widely your core modules are being
                  adopted by consumers.</p>
                  <p>A sustained <strong>drop</strong> on one line isn't just quieter usage:
                  it often means a recently published version regressed, the module was
                  deprecated, or consumers have pinned an older version and stopped
                  upgrading. Steady or rising downloads on your core modules is the healthy
                  signal.</p>
                </>
              }
            />
            <TopTrendChart
              kicker="State Trend"
              title="Top workspace state pushes (90 days)"
              helpTrack="help_state_pushes_trend"
              entities={a?.top_workspace_push_series ?? []}
              unit="pushes"
              legendHref={slug ? (name) => `/org/${slug}/state/${encodeURIComponent(name)}` : undefined}
              help={
                <>
                  <p>Daily state pushes for your <strong>5 most-active workspaces</strong> over
                  the last 90 days - one push per successful apply against a workspace.</p>
                  <p>High churn on one line can mean noisy, over-frequent applies or ongoing
                  drift remediation - worth investigating if a single workspace dominates.
                  Conversely, <strong>zero</strong> pushes on a workspace you expect to be
                  active usually means CI isn't wired up to push state, so applies are
                  happening outside Terramantle's visibility.</p>
                </>
              }
            />
          </div>

          {/* State push cadence + slowest applies. Paired because they answer the two halves
              of one question: how much is this org applying, and what does an apply cost when
              it runs. Cadence sits left, where the reading starts, since it is the volume the
              runtime is multiplied by. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <PushCadenceCard series={a?.state_daily_series ?? []} />
            <SlowestAppliesCard applies={a?.slowest_applies ?? []} orgSlug={slug} />
          </div>
          </PageSection>

          {/* ── Risk ─────────────────────────────────────────────────────────
              What is exposed right now — the section a reader may actually need to act on,
              placed after Activity so the numbers in it already have a sense of scale.

              The two findings rows sit here rather than below the trend charts, where they used
              to: "Open findings · modules" is the severity breakdown of the very list directly
              above it, and putting ninety days of download lines between them meant the one
              card that explains another was the furthest thing from it. */}
          <PageSection title="Risk">
            {((a?.top_risk_modules?.length ?? 0) > 0 || (a?.top_risk_workspaces?.length ?? 0) > 0) ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                {(a?.top_risk_modules?.length ?? 0) > 0 && (
                  <TopRiskModulesCard modules={a!.top_risk_modules} />
                )}
                {(a?.top_risk_workspaces?.length ?? 0) > 0 && (
                  <TopRiskWorkspacesCard workspaces={a!.top_risk_workspaces} />
                )}
              </div>
            ) : (
              <RiskAllClearCard />
            )}

            {/* Open findings: modules (left) | state (right) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
              <OpenFindingsCard
                title="Open findings · modules"
                helpTrack="help_module_scan_findings"
                findings={a?.module_findings_by_severity ?? []}
                drilldown={(a?.module_findings_artifacts ?? []).map((s) => ({
                  severity: s.severity,
                  total_artifacts: s.total_artifacts,
                  items: s.artifacts.map((m) => ({
                    label: `${m.name}/${m.provider} v${m.version}`,
                    href: `/org/${m.namespace}/modules/${m.name}/${m.provider}/${m.version}`,
                    count: m.count,
                  })),
                }))}
                help={
                  <>
                    <p>The severity distribution of policy and security findings currently open
                    on each module's <strong>latest published version</strong> - the same scope
                    as the "Highest risk modules" card above.</p>
                    <p>This is risk waiting to be consumed: concentration in
                    <strong> critical / high</strong> should block (or already blocks)
                    consumption - fix and republish, and the ring empties.</p>
                  </>
                }
              />
              <OpenFindingsCard
                title="Open findings · state"
                helpTrack="help_state_scan_findings"
                findings={a?.state_findings_by_severity ?? []}
                drilldown={(a?.state_findings_artifacts ?? []).map((s) => ({
                  severity: s.severity,
                  total_artifacts: s.total_artifacts,
                  items: s.artifacts.map((w) => ({
                    label: w.workspace_name,
                    href: `/org/${w.org_slug}/state/${encodeURIComponent(w.workspace_name)}`,
                    count: w.count,
                  })),
                }))}
                help={
                  <>
                    <p>The severity distribution of findings open against each workspace's
                    <strong> current state</strong> - the same scope as the "Highest risk state
                    workspaces" card above.</p>
                    <p>Unlike module findings, these reflect risk already
                    <strong> live in your infrastructure</strong>: remediate and apply, and the
                    next state push clears them.</p>
                  </>
                }
              />
            </div>

            {/* Continuous-patching exposure. */}
            <PatchAdoptionCard adoption={a?.patch_adoption} />
          </PageSection>

          {/* ── Policy posture ───────────────────────────────────────────────
              The only section that says what was actually STOPPED rather than what exists.

              It used to LEAD the page on that reasoning, and the reasoning was better than the
              placement: four bare figures above the fold, with no heading, read as a status bar
              the eye skips rather than a section. Down here it is a labelled conclusion to the
              risk and activity above it, which is also the order a reader asks the questions in
              — what is exposed, how busy are we, and what did the gates actually refuse.

              Same four figures as Policies -> Overview, read from the same endpoints. */}
          {slug && (
            <PageSection title="Policy posture" note="what the gates refused">
              <PolicyPostureStrip orgSlug={slug} />
            </PageSection>
          )}

          {/* ── Footprint ────────────────────────────────────────────────────
              Inventory, not risk or activity: what this org manages. Last because it is the
              section you consult, not one you monitor. */}
          <PageSection title="Footprint">
            <ManagedResourcesCard state={a?.state} footprint={a?.footprint} />
          </PageSection>
        </>
      )}
    </Layout>
  )
}
